import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import { SxProps, Theme } from '@mui/material/styles';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import {
  FieldValues,
  useController,
  UseControllerProps,
  UseControllerReturn,
} from 'react-hook-form';

import Label from './Label';
import { TextFieldInputSlotProps } from './TextFieldInputSlotProps';

type Props<T extends FieldValues> = UseControllerProps<T> &
  Pick<TextFieldProps, 'placeholder'> & {
    controller?: UseControllerReturn<T>;
    dataUi?: string;
    displayValue?: string | number;
    isDisabled?: boolean;
    isInline?: boolean;
    isRequired?: boolean;
    label?: React.ReactNode;
    manualError?: string;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    slotProps?: TextFieldInputSlotProps;
    suppressErrorMessage?: boolean;
    sx?: SxProps<Theme>;
  };

export default function NumberInput<T extends FieldValues>(props: Props<T>) {
  return props.controller ? (
    <NumberInputField {...props} controller={props.controller} />
  ) : (
    <ControlledNumberInput {...props} />
  );
}

function ControlledNumberInput<T extends FieldValues>(props: Props<T>) {
  const controller = useController({ control: props.control, name: props.name });

  return <NumberInputField {...props} controller={controller} />;
}

function NumberInputField<T extends FieldValues>({
  controller: { field, fieldState },
  dataUi,
  displayValue,
  isDisabled,
  isInline,
  isRequired,
  label,
  manualError,
  name,
  onChange,
  placeholder,
  slotProps,
  suppressErrorMessage,
  sx,
}: Props<T> & { controller: UseControllerReturn<T> }) {
  const error =
    manualError ?? fieldState.error?.message ?? fieldState.error?.root?.message;
  const value = displayValue ?? field.value ?? '';

  return (
    <FormControl
      disabled={isDisabled}
      error={Boolean(error)}
      fullWidth={!isInline}
      sx={[
        isInline
          ? { alignItems: 'center', flexDirection: 'row', flexWrap: 'wrap', gap: 1 }
          : { display: 'flex', flexDirection: 'column', gap: 0.25 },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {label && <Label isRequired={isRequired}>{label}</Label>}

      <TextField
        className={isRequired ? 'required' : ''}
        disabled={isDisabled}
        error={Boolean(error)}
        fullWidth={!isInline}
        id={name}
        onChange={onChange ?? field.onChange}
        onKeyDown={(event) => {
          if (event.key === 'e' || event.key === 'E') {
            event.preventDefault();
          }
        }}
        onWheel={(event) => (event.target as HTMLElement)?.blur()}
        placeholder={placeholder}
        slotProps={{
          ...slotProps,
          htmlInput: { ...slotProps?.htmlInput, ...(dataUi && { 'data-ui': dataUi }) },
        }}
        type="number"
        value={value}
      />

      {error && !suppressErrorMessage && (
        <FormHelperText sx={{ m: 0, fontSize: '10px', fontWeight: 700, width: '100%' }}>
          {error}
        </FormHelperText>
      )}
    </FormControl>
  );
}
